import { useMemo } from 'react';
import NotifyUtils from 'utils/NotifyUtils';
import { useStore } from 'zustand-lib/storeGlobal';

// This interface only using for config hang diem store

export interface IConfig {
  badge: {
    text: string;
    styles: Record<string, string>;
  };
  label: {
    text: string;
    styles: Record<string, string>;
  };
  tag: {
    url: string;
  };
  headerSellerBadge: {
    text: string;
    styles: Record<string, string>;
  };
  linkRedirect: string
}

interface IInternalConfig {
  CONFIG_STORE: {
    [key: string]: IConfig;
  };
  NEW_TAG_HANG_DIEM: string;
}

interface IAppState {
  [key: string]: IInternalConfig;
}

export interface IIconSetting {
  backgroundColor: string;
  code: string;
  horizontalIconSvg: string;
  name: string;
  preIcon: string;
  preIconSvg: string;
  slug: string;
  textColor: string;
  visible: boolean;
}

interface IReplaceHangDiemSetting {
  [key: string]: IIconSetting;
}

interface IProps {
  sellerCode?: string;
  vendorInfoSellerCode?: string;
}

const INTERNAL_CONFIG_TAG_KEY = 'CONFIG_PRODUCT_TAG';
const defaultConfig: IConfig = {
  badge: {
    text: '',
    styles: {},
  },
  label: {
    text: '',
    styles: {},
  },
  tag: {
    url: '',
  },
  headerSellerBadge: {
    text: '',
    styles: {},
  },
  linkRedirect: '',
};

export function useConfigHangDiem({ sellerCode = '', vendorInfoSellerCode = '' }: IProps) {
  
  const appSetting = useStore((state: IAppState) => state.appSetting);
  const iconHangDiem = useStore((state: IReplaceHangDiemSetting) => state.replaceHangDiemSetting);
  const internalTagConfig = (appSetting as unknown as Record<string, IInternalConfig>)[INTERNAL_CONFIG_TAG_KEY];

  const reMapConfig = (type: 'sellerCode' | 'vendorSellerCode') => {
    const reMapConfig: Record<string, IConfig> = {};

    if (type === 'sellerCode') {
      Object.entries(internalTagConfig?.['CONFIG_STORE'])?.forEach(([key, value]) => {
        const sellerCode = key?.split('/')?.[0];
        if (sellerCode) {
          reMapConfig[sellerCode] = value;
        }
      });
    } else if (type === 'vendorSellerCode') {
      Object.entries(internalTagConfig?.['CONFIG_STORE'])?.forEach(([key, value]) => {
        const vendorSellerCode = key?.split('/')?.[1];
        if (vendorSellerCode) {
          reMapConfig[vendorSellerCode] = value;
        }
      });
    }

    return reMapConfig || {};
  };

  const isHangDiem = useMemo(() => {
    if (!internalTagConfig || (!sellerCode && !vendorInfoSellerCode) || typeof internalTagConfig !== 'object') {
      return false;
    }
    if (vendorInfoSellerCode !== '') {
      const mapVendorSellerCode = reMapConfig('vendorSellerCode');
      return Object.keys(mapVendorSellerCode)?.includes(vendorInfoSellerCode) ?? false;
    } else if (sellerCode !== '') {
      const mapSellerCode = reMapConfig('sellerCode');
      return Object.keys(mapSellerCode)?.includes(sellerCode) ?? false;
    }

    return false;
  }, [internalTagConfig, sellerCode, vendorInfoSellerCode]);

  const config: IConfig = useMemo(() => {
    if (!internalTagConfig || (!sellerCode && !vendorInfoSellerCode) || typeof internalTagConfig !== 'object') {
      return defaultConfig;
    }

    if (vendorInfoSellerCode !== '') {
      const mapVendorSellerCode = reMapConfig('vendorSellerCode');
      return (mapVendorSellerCode as Record<string, IConfig>)?.[vendorInfoSellerCode] ?? defaultConfig;
    } else if (sellerCode !== '') {
      const mapSellerCode = reMapConfig('sellerCode');
      return (mapSellerCode as Record<string, IConfig>)?.[sellerCode] ?? defaultConfig;
    }

    return defaultConfig;
  }, [internalTagConfig, sellerCode, vendorInfoSellerCode]);

  return {
    isHangDiem,
    config,
    iconHangDiem,
    linkRedirect: config?.linkRedirect || '',
  };
}
